import { ApiDocEndpoint, ApiDocParam, ApiHistoryEntry } from '@/types/ApiDocs.ts';

// ─── קבועים ────────────────────────────────────────────────────────────
export const LS_KEY = 'api_docs_history';
export const HISTORY_MAX_DAYS = 3;

export const METHOD_COLORS: Record<string, string> = {
    GET: 'bg-blue-100 text-blue-700 border-blue-200',
    POST: 'bg-green-100 text-green-700 border-green-200',
    PUT: 'bg-amber-100 text-amber-700 border-amber-200',
    PATCH: 'bg-purple-100 text-purple-700 border-purple-200',
    DELETE: 'bg-red-100 text-red-700 border-red-200',
};

// ─── היסטוריה ────────────────────────────────────────────────────────────
export function loadHistory(): ApiHistoryEntry[] {
    try {
        const raw = localStorage.getItem(LS_KEY);
        if (!raw) return [];
        const all: ApiHistoryEntry[] = JSON.parse(raw);
        const cutoff = Date.now() - HISTORY_MAX_DAYS * 24 * 60 * 60 * 1000;
        return all.filter((e) => e.timestamp >= cutoff);
    } catch {
        return [];
    }
}

export function saveHistory(entries: ApiHistoryEntry[]) {
    localStorage.setItem(LS_KEY, JSON.stringify(entries));
}

// ─── פענוח ערך פרמטר לפי סוג ───────────────────────────────────────────────
// עבור type='array'/'keyvalue' הערך המאוחסן ב-state הוא מחרוזת JSON (array/object
// בהתאמה) - יש לפענח אותה בפועל לפני הכנסה ל-body, אחרת נשלחת מחרוזת JSON
// מקוננת ("stringified") במקום מערך/אובייקט אמיתי.
export function resolveParamValue(param: ApiDocParam, raw: string): unknown {
    if (!raw) return undefined;
    if (param.type === 'array' || param.type === 'keyvalue') {
        try {
            return JSON.parse(raw);
        } catch {
            return undefined;
        }
    }
    return raw;
}

// ─── בניית דוגמאות קוד ───────────────────────────────────────────────────
export function buildCodeSnippets(
    endpoint: ApiDocEndpoint,
    token: string,
    paramVals: Record<string, string>,
) {
    let resolvedUrl = endpoint.url_pattern;
    const pathParams = endpoint.params_schema.filter((p) => p.in === 'path');
    pathParams.forEach((p) => {
        resolvedUrl = resolvedUrl.replace(`{${p.name}}`, paramVals[p.name] || `:${p.name}`);
    });

    const queryParams = endpoint.params_schema.filter((p) => p.in !== 'path' && endpoint.method === 'GET');
    const bodyParams = endpoint.params_schema.filter((p) => p.in !== 'path' && endpoint.method !== 'GET');

    const fullBase = `${window.location.origin}${resolvedUrl}`;

    const qp = new URLSearchParams();
    queryParams.forEach((p) => {
        const v = paramVals[p.name] || p.default || '';
        if (v) qp.set(p.name, v);
    });
    const qs = qp.toString() ? `?${qp.toString()}` : '';

    const bodyObj: Record<string, unknown> = {};
    bodyParams.forEach((p) => {
        const raw = paramVals[p.name] || p.default || '';
        const v = resolveParamValue(p, raw);
        if (v !== undefined && v !== '') bodyObj[p.name] = v;
    });

    const authHeader = token ? `-H "Authorization: Token ${token}"` : '';

    let curlCode = `curl -X ${endpoint.method} "${fullBase}${qs}" \\\n  ${authHeader} \\\n  -H "Accept: application/json"`;
    if (endpoint.method !== 'GET' && Object.keys(bodyObj).length > 0) {
        curlCode += ` \\\n  -H "Content-Type: application/json" \\\n  -d '${JSON.stringify(bodyObj, null, 2)}'`;
    }

    let pythonCode = `import requests\n\nurl = "${fullBase}"\n`;
    if (token) pythonCode += `headers = {"Authorization": "Token ${token}", "Accept": "application/json"}\n`;
    else pythonCode += `headers = {"Accept": "application/json"}\n`;

    if (endpoint.method === 'GET') {
        const paramsStr = JSON.stringify(Object.fromEntries(qp), null, 4);
        pythonCode += `params = ${paramsStr}\n\nresponse = requests.get(url, headers=headers, params=params)\nprint(response.json())`;
    } else {
        pythonCode += `payload = ${JSON.stringify(bodyObj, null, 4)}\n\nresponse = requests.${endpoint.method.toLowerCase()}(url, headers=headers, json=payload)\nprint(response.json())`;
    }

    return { curl: curlCode, python: pythonCode };
}

// ─── ייצוא תיעוד אנדפוינט כ-Markdown ────────────────────────────────────
export function buildMarkdownDoc(endpoint: ApiDocEndpoint, token: string): string {
    const fullUrl = `${window.location.origin}${endpoint.url_pattern}`;
    const snippets = buildCodeSnippets(endpoint, token, {});
    const lines: string[] = [];

    lines.push(`# ${endpoint.name}`, '');
    lines.push(`**Method:** \`${endpoint.method}\``, '');
    lines.push(`**URL:** \`${fullUrl}\``, '');
    lines.push(`**דורש אימות:** ${endpoint.auth_required ? 'כן' : 'לא'}`);
    if (endpoint.is_admin_only) lines.push(`**הרשאת מנהל בלבד:** כן`);
    lines.push('');

    if (endpoint.description) {
        lines.push('## תיאור', '', endpoint.description, '');
    }

    if (endpoint.params_schema.length > 0) {
        lines.push('## פרמטרים', '');
        lines.push('| שם | מיקום | סוג | חובה | ברירת מחדל | תיאור |');
        lines.push('|---|---|---|---|---|---|');
        endpoint.params_schema.forEach((p) => {
            lines.push(
                `| \`${p.name}\` | ${p.in ?? '-'} | ${p.type} | ${p.required ? 'כן' : 'לא'} | ${p.default ?? '-'} | ${p.description ?? '-'} |`,
            );
        });
        lines.push('');
    }

    lines.push('## דוגמת קוד - cURL', '', '```bash', snippets.curl, '```', '');
    lines.push('## דוגמת קוד - Python', '', '```python', snippets.python, '```', '');

    if (endpoint.response_example) {
        lines.push(
            '## דוגמת תגובה',
            '',
            '```json',
            JSON.stringify(endpoint.response_example, null, 2),
            '```',
            '',
        );
    }

    return lines.join('\n');
}

export function endpointMarkdownFilename(endpoint: ApiDocEndpoint): string {
    const safe = endpoint.name.replace(/[\\/:*?"<>|]+/g, '-').trim();
    return `${safe || 'endpoint'}.md`;
}

export function downloadMarkdown(filename: string, content: string) {
    const blob = new Blob([content], { type: 'text/markdown;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}
